import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CheckCircle2, Download, Filter, Mail, Receipt, UsersRound, WalletCards, X } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency } from '../utils/formatters';

const csvCell = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;

const philippineDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit',
});

function philippineDateValue() {
  const parts = Object.fromEntries(philippineDateFormatter.formatToParts(new Date()).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function initialRange() {
  const endValue = philippineDateValue();
  const end = new Date(`${endValue}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - 29);
  return { start: start.toISOString().slice(0, 10), end: endValue };
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-PH', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function statusClass(status) {
  const normalised = String(status || '').toLowerCase();
  if (normalised === 'cancelled') return 'bg-red-50 text-red-700';
  if (normalised === 'completed') return 'bg-blue-50 text-blue-700';
  if (normalised === 'confirmed' || normalised === 'checkedin') return 'bg-emerald-50 text-emerald-700';
  return 'bg-amber-50 text-amber-700';
}

export default function BookingDetailsReport() {
  const [range, setRange] = useState(initialRange);
  const [dateBasis, setDateBasis] = useState('booking');
  const [ownerId, setOwnerId] = useState('');
  const [courtId, setCourtId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [bookingStatus, setBookingStatus] = useState('active');
  const [options, setOptions] = useState({ owners: [], courts: [], paymentMethods: [], bookingStatuses: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveringId, setRecoveringId] = useState(null);
  const [recoveryBooking, setRecoveryBooking] = useState(null);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);

  const courts = useMemo(
    () => (options.courts || []).filter((court) => !ownerId || court.ownerId === ownerId),
    [options.courts, ownerId],
  );

  async function loadFilters() {
    try {
      const data = await adminService.getBookingDetailsFilterOptions();
      setOptions({ owners: data?.owners || [], courts: data?.courts || [], paymentMethods: data?.paymentMethods || [], bookingStatuses: data?.bookingStatuses || [] });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load report filters');
    }
  }

  async function loadReport(selectedRange = range) {
    if (selectedRange.end < selectedRange.start) {
      toast.error('End date must be on or after the start date');
      return;
    }
    setLoading(true);
    try {
      const data = await adminService.getBookingDetailsReport({
        startDate: selectedRange.start,
        endDate: selectedRange.end,
        dateBasis,
        ...(ownerId ? { ownerId } : {}),
        ...(courtId ? { courtId } : {}),
        ...(paymentMethod ? { paymentMethod } : {}),
        bookingStatus,
      });
      setReport(data);
    } catch (error) {
      setReport(null);
      toast.error(error.response?.data?.message || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  }

  function showToday() {
    const today = philippineDateValue();
    const todayRange = { start: today, end: today };
    setRange(todayRange);
    loadReport(todayRange);
  }

  useEffect(() => {
    loadFilters();
    loadReport();
  }, []);

  function exportCsv() {
    if (!report) return;
    const rows = [
      ['FREAC booking details report'],
      ['Period', `${String(report.period?.startDate || range.start).slice(0, 10)} to ${String(report.period?.endDate || range.end).slice(0, 10)}`],
      ['Date range based on', report.period?.dateBasis === 'purchased' ? 'Purchased date & time' : 'Booking date & time'],
      ['Venue owner', (options.owners || []).find((owner) => owner.id === ownerId)?.name || 'All venue owners'],
      ['Court', courts.find((court) => court.id === courtId)?.name || 'All courts'],
      ['Payment method', paymentMethod || 'All payment methods'],
      ['Booking status', (options.bookingStatuses || []).find((status) => status.value === bookingStatus)?.label || 'Active (exclude cancelled)'],
      ['Total rental', report.summary?.totalRental ?? 0],
      [],
      ['Booking date & time', 'Purchased at', 'Type', 'Open Play session', 'Venue owner', 'Venue', 'Court', 'Player', 'Email', 'Phone', 'Booking status', 'Payment method', 'Payment status', 'Collection', 'Funds location', 'Funds status', 'Payout reference', 'Rental amount', 'Convenience Fee', 'Total paid', 'Online venue earnings', 'Payment reference'],
      ...(report.bookings || []).map((booking) => [
        formatDateTime(booking.startTime), formatDateTime(booking.purchasedAt), booking.bookingType, booking.openPlayName, booking.ownerName, booking.venueName, booking.courtName,
        booking.playerName, booking.playerEmail, booking.playerPhoneNumber, booking.status,
        booking.paymentMethod, booking.paymentStatus, booking.settlementLabel, booking.fundsLocation,
        booking.fundsStatus, booking.payoutReference, booking.rentalAmount,
        booking.convenienceFee, booking.totalPaid, booking.payableByFreac, booking.paymentReference,
      ]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `freac-booking-details-${range.start}-to-${range.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function openRecoveryDialog(booking) {
    setRecoveryBooking(booking);
    setSendConfirmationEmail(true);
  }

  function closeRecoveryDialog() {
    if (recoveringId) return;
    setRecoveryBooking(null);
  }

  async function recoverPaidBooking() {
    if (!recoveryBooking) return;
    const recoveryId = recoveryBooking.paymentBookingId || recoveryBooking.id;
    setRecoveringId(recoveryId);
    try {
      const result = await adminService.recoverPaidCancelledBooking(recoveryId, sendConfirmationEmail);
      toast.success(result.message || 'Paid booking restored');
      setRecoveryBooking(null);
      await loadReport();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not recover this booking');
    } finally {
      setRecoveringId(null);
    }
  }

  const summary = report?.summary || {};
  const summaryCards = [
    { label: 'Bookings', value: summary.totalBookings ?? 0, icon: CalendarDays, color: 'bg-[#EEF4FF] text-[#175CD3]' },
    { label: 'Total rental', value: formatCurrency(summary.totalRental), icon: WalletCards, color: 'bg-[#FFF4E5] text-[#B54708]' },
      { label: 'Received directly', value: formatCurrency(summary.venueDirectCollections), icon: Receipt, color: 'bg-[#E6F4F1] text-[#0B7F83]' },
      { label: 'Total venue earnings', value: formatCurrency(summary.totalVenueEarnings ?? ((summary.payableByFreac || 0) + (summary.venueDirectCollections || 0))), icon: WalletCards, color: 'bg-[#F4F3FF] text-[#6938EF]' },
      { label: 'Awaiting payout', value: formatCurrency(summary.awaitingPayout ?? summary.payMongoSettlementPending), icon: WalletCards, color: 'bg-[#FFF4E5] text-[#B54708]' },
      { label: 'Payout in progress', value: formatCurrency(summary.payoutInProgress ?? summary.freacHeld), icon: WalletCards, color: 'bg-[#EEF4FF] text-[#175CD3]' },
      { label: 'Paid to venues', value: formatCurrency(summary.paidToVenue ?? summary.ownerReceived), icon: Receipt, color: 'bg-[#DCFCE7] text-[#166534]' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Booking Details Report</h1>
          <p className="mt-1 text-sm text-[#667085]">Review individual court bookings and Open Play registrations by booking or purchase date.</p>
        </div>
        <button onClick={exportCsv} disabled={!report} className="admin-btn-secondary inline-flex h-10 items-center gap-2 px-4 disabled:opacity-50">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <section className="admin-card">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]"><Filter size={17} className="text-[#0B7F83]" /> Report filters</div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
          <label className="text-xs font-medium text-[#475467]">Venue owner
            <select value={ownerId} onChange={(event) => { setOwnerId(event.target.value); setCourtId(''); }} className="admin-input mt-1 h-10 w-full">
              <option value="">All venue owners</option>
              {(options.owners || []).map((owner) => <option key={owner.id} value={owner.id}>{owner.name} — {owner.email}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">Court
            <select value={courtId} onChange={(event) => setCourtId(event.target.value)} className="admin-input mt-1 h-10 w-full">
              <option value="">All courts</option>
              {courts.map((court) => <option key={court.id} value={court.id}>{court.venueName} — {court.name}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">Payment method
            <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} className="admin-input mt-1 h-10 w-full">
              <option value="">All payment methods</option>
              {(options.paymentMethods || []).map((method) => <option key={method} value={method}>{method}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">Booking status
            <select value={bookingStatus} onChange={(event) => setBookingStatus(event.target.value)} className="admin-input mt-1 h-10 w-full">
              {(options.bookingStatuses || []).map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">Date range based on
            <select value={dateBasis} onChange={(event) => setDateBasis(event.target.value)} className="admin-input mt-1 h-10 w-full">
              <option value="booking">Booking date & time</option>
              <option value="purchased">Purchased date & time</option>
            </select>
          </label>
          <label className="text-xs font-medium text-[#475467]">From
            <input type="date" value={range.start} max={range.end} onChange={(event) => setRange((current) => ({ ...current, start: event.target.value }))} className="admin-input mt-1 h-10 w-full" />
          </label>
          <label className="text-xs font-medium text-[#475467]">To
            <input type="date" value={range.end} min={range.start} onChange={(event) => setRange((current) => ({ ...current, end: event.target.value }))} className="admin-input mt-1 h-10 w-full" />
          </label>
          <div className="flex gap-2 self-end"><button type="button" onClick={showToday} className="admin-btn-secondary h-10 px-4">Today</button><button type="button" onClick={() => loadReport()} className="admin-btn-primary h-10 px-4">Apply filters</button></div>
        </div>
      </section>

      {loading ? (
        <div className="admin-card flex justify-center py-14"><div className="h-8 w-8 animate-spin rounded-full border-4 border-admin-600 border-t-transparent" /></div>
      ) : !report ? null : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
            {summaryCards.map((card) => <div key={card.label} className="admin-card flex items-center gap-4"><div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.color}`}><card.icon size={21} /></div><div><p className="text-xs text-[#667085]">{card.label}</p><p className="mt-1 text-xl font-bold text-[#1F2937]">{card.value}</p></div></div>)}
          </div>

          <section className="admin-card">
            <div className="mb-4 flex items-center justify-between gap-4"><div><h2 className="font-semibold text-[#1F2937]">Booking records</h2><p className="text-xs text-[#667085]">{report.bookings?.length || 0} booking{report.bookings?.length === 1 ? '' : 's'} {report.period?.dateBasis === 'purchased' ? 'purchased' : 'scheduled'} in the selected period.</p></div><UsersRound size={20} className="text-[#0B7F83]" /></div>
            <div className="overflow-x-auto">
              <table className="admin-table min-w-[2240px]"><thead><tr><th>Booking date & time</th><th>Purchased at</th><th>Type</th><th>Venue owner</th><th>Venue / Court</th><th>Player</th><th>Status</th><th>Payment</th><th>Collection</th><th>Funds</th><th>Rental</th><th>Convenience Fee</th><th>Total paid</th><th>Reference</th><th>Actions</th></tr></thead>
                <tbody>{report.bookings?.length ? report.bookings.map((booking) => <tr key={booking.id}>
                  <td className="whitespace-nowrap font-medium text-[#344054]">{formatDateTime(booking.startTime)}<p className="mt-0.5 text-xs font-normal text-[#667085]">to {formatDateTime(booking.endTime).split(', ').pop()}</p></td>
                  <td className="whitespace-nowrap text-[#344054]">{formatDateTime(booking.purchasedAt)}</td>
                  <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.bookingType === 'Open Play' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-700'}`}>{booking.bookingType || 'Court Booking'}</span></td>
                  <td><p className="font-medium text-[#1F2937]">{booking.ownerName || '—'}</p></td>
                  <td><p className="font-medium text-[#1F2937]">{booking.venueName}</p><p className="text-xs text-[#667085]">{booking.openPlayName ? `${booking.openPlayName} · ${booking.courtName}` : booking.courtName}</p></td>
                  <td><p className="font-medium text-[#1F2937]">{booking.playerName || '—'}</p><p className="text-xs text-[#667085]">{booking.playerEmail}</p><p className="text-xs text-[#667085]">{booking.playerPhoneNumber || ''}</p></td>
                  <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(booking.status)}`}>{booking.status}</span></td>
                  <td><p className="text-sm font-medium text-[#344054]">{booking.paymentMethod || '—'}</p><p className="text-xs text-[#667085]">{booking.paymentStatus}</p></td>
                  <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.collectionChannel === 'VenueDirect' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{booking.settlementLabel}</span></td>
                  <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.fundsLocation === 'VenueOwner' ? 'bg-emerald-50 text-emerald-700' : booking.fundsLocation === 'FREAC' ? 'bg-blue-50 text-blue-700' : booking.fundsLocation === 'PayMongo' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{booking.fundsLocation}</span><p className="mt-1 max-w-[220px] text-xs text-[#667085]">{booking.fundsStatus}</p>{booking.payoutReference && <p className="mt-1 max-w-[180px] truncate font-mono text-[10px] text-[#667085]" title={booking.payoutReference}>Payout: {booking.payoutReference}</p>}</td>
                  <td className="whitespace-nowrap">{formatCurrency(booking.rentalAmount)}</td>
                  <td className="whitespace-nowrap">{booking.convenienceFeeCoveredByVenue ? 'Covered by venue' : formatCurrency(booking.convenienceFee)}</td>
                  <td className="whitespace-nowrap font-semibold text-[#0B7F83]">{formatCurrency(booking.totalPaid)}</td>
                  <td className="max-w-[180px] truncate font-mono text-xs text-[#667085]" title={booking.paymentReference || ''}>{booking.paymentReference || '—'}</td>
                  <td>{booking.bookingType !== 'Open Play' && booking.status === 'Cancelled' && booking.paymentMethod === 'GCash' ? <button type="button" onClick={() => openRecoveryDialog(booking)} disabled={recoveringId === (booking.paymentBookingId || booking.id)} className="admin-btn-primary whitespace-nowrap px-3 py-1.5 text-xs"><span className="flex items-center gap-1"><CheckCircle2 size={14} />{recoveringId === (booking.paymentBookingId || booking.id) ? 'Verifying…' : 'Verify & confirm'}</span></button> : '—'}</td>
                </tr>) : <tr><td colSpan="15" className="py-12 text-center text-sm text-[#667085]">No bookings or Open Play registrations match these filters.</td></tr>}</tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {recoveryBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeRecoveryDialog(); }}>
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="recover-booking-title">
            <div className="flex items-start justify-between border-b border-[#EAECF0] px-6 py-5">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700"><AlertTriangle size={22} /></div>
                <div><h2 id="recover-booking-title" className="text-lg font-bold text-[#1F2937]">Verify payment and confirm booking?</h2><p className="mt-1 text-sm text-[#667085]">This action checks the transaction directly with PayMongo.</p></div>
              </div>
              <button type="button" onClick={closeRecoveryDialog} disabled={Boolean(recoveringId)} className="rounded-lg p-1.5 text-[#667085] hover:bg-[#F2F4F7] disabled:opacity-40" aria-label="Close dialog"><X size={19} /></button>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div className="rounded-xl border border-[#EAECF0] bg-[#F9FAFB] p-4 text-sm">
                <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-2"><span className="text-[#667085]">Player</span><span className="font-semibold text-[#344054]">{recoveryBooking.playerName || '—'}</span><span className="text-[#667085]">Venue / Court</span><span className="font-semibold text-[#344054]">{recoveryBooking.venueName} · {recoveryBooking.courtName}</span><span className="text-[#667085]">Schedule</span><span className="font-semibold text-[#344054]">{formatDateTime(recoveryBooking.startTime)}</span><span className="text-[#667085]">Reference</span><span className="break-all font-mono text-xs text-[#344054]">{recoveryBooking.paymentReference || '—'}</span></div>
              </div>

              <p className="text-sm leading-6 text-[#475467]">The booking will be restored only when PayMongo confirms a successful payment and every linked court slot is still available.</p>

              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#D0D5DD] p-4 hover:bg-[#F9FAFB]">
                <input type="checkbox" checked={sendConfirmationEmail} onChange={(event) => setSendConfirmationEmail(event.target.checked)} disabled={Boolean(recoveringId)} className="mt-0.5 h-4 w-4 rounded border-[#98A2B3] text-[#0B7F83] focus:ring-[#0B7F83]" />
                <Mail size={18} className="mt-0.5 shrink-0 text-[#0B7F83]" />
                <span><span className="block text-sm font-semibold text-[#344054]">Send booking email notifications</span><span className="mt-0.5 block text-xs leading-5 text-[#667085]">Enabled by default. Turn this off to suppress emails to both the customer and venue owner.</span></span>
              </label>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-[#EAECF0] bg-[#F9FAFB] px-6 py-4 sm:flex-row sm:justify-end">
              <button type="button" onClick={closeRecoveryDialog} disabled={Boolean(recoveringId)} className="admin-btn-secondary px-4 py-2 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={recoverPaidBooking} disabled={Boolean(recoveringId)} className="admin-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2 disabled:opacity-60"><CheckCircle2 size={16} />{recoveringId ? 'Verifying with PayMongo…' : 'Verify and confirm booking'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
